<template>
  <transition>
    <button v-if="feedbackIsOpen" class="a-overlay" @click="toggleFeedback"></button>
  </transition>

  <div
    class="o-feedback"
    :class="{
      'is-open': feedbackIsOpen,
      'o-feedback--success': isSuccess,
      'o-feedback--error': isError,
    }"
  >
    <button class="o-feedback__action" @click="toggleFeedback" type="button">
      <div class="o-feedback__action-inner">
        <span class="o-feedback__action-label" v-text="feedbackLabel"></span>
      </div>
    </button>

    <button type="button" class="o-feedback__close" @click="toggleFeedback">
      <span class="u-screen-reader-only" v-text="$t('feedback.close')"></span>
      <svg class="o-feedback__close-svg" aria-hidden="true">
        <use :xlink:href="$root.spritePath + '#close'" />
      </svg>
    </button>

    <form @submit.prevent="submit" class="o-feedback__content">
      <h2 class="m-feedback__title a-h3" v-text="isSuccess ? successTitle : feedbackTitle"></h2>

      <div v-if="Object.keys(errors).length" class="m-feedback__error">
        {{ Object.values(errors)[0][0] }}
      </div>
      <div v-else class="m-feedback__text o-editor-content" v-text="isSuccess ? successText : feedbackText"></div>

      <fieldset v-show="! isSuccess" class="m-feedback__fieldset" @change="showForm">
        <div class="m-feedback__option is-bad">
          <label class="m-feedback__option-label" for="feedback-worst">
            <svg class="m-feedback__option-svg" aria-title="Worst">
              <use :xlink:href="$root.spritePath + '#feedback-worst'" />
            </svg>
          </label>
          <input
            v-model="rating"
            class="m-feedback__option-radio"
            id="feedback-worst"
            type="radio"
            name="feedback"
            value="worst"
          >
        </div>
        <div class="m-feedback__option is-bad">
          <label class="m-feedback__option-label" for="feedback-worse">
            <svg class="m-feedback__option-svg" aria-title="worse">
              <use :xlink:href="$root.spritePath + '#feedback-worse'" />
            </svg>
          </label>
          <input
            v-model="rating"
            class="m-feedback__option-radio"
            id="feedback-worse"
            type="radio"
            name="feedback"
            value="worse"
          >
        </div>
        <div class="m-feedback__option is-good">
          <label class="m-feedback__option-label" for="feedback-normal">
            <svg class="m-feedback__option-svg" aria-title="normal">
              <use :xlink:href="$root.spritePath + '#feedback-normal'" />
            </svg>
          </label>
          <input
            v-model="rating"
            class="m-feedback__option-radio"
            id="feedback-normal"
            type="radio"
            name="feedback"
            value="normal"
          >
        </div>
        <div class="m-feedback__option is-good">
          <label class="m-feedback__option-label" for="feedback-better">
            <svg class="m-feedback__option-svg" aria-title="better">
              <use :xlink:href="$root.spritePath + '#feedback-better'" />
            </svg>
          </label>
          <input
            v-model="rating"
            class="m-feedback__option-radio"
            id="feedback-better"
            type="radio"
            name="feedback"
            value="better"
          >
        </div>
        <div class="m-feedback__option is-good">
          <label class="m-feedback__option-label" for="feedback-best">
            <svg class="m-feedback__option-svg" aria-title="best">
              <use :xlink:href="$root.spritePath + '#feedback-best'" />
            </svg>
          </label>
          <input
            v-model="rating"
            class="m-feedback__option-radio"
            id="feedback-best"
            type="radio"
            name="feedback"
            value="best"
          >
        </div>
      </fieldset>
      <transition>
        <div v-show="rating || ! isSuccess" class="m-feedback__form" :style="{ 'flex-basis': formHeight + 'px' }">
          <div class="m-feedback__form-inner" ref="innerForm">
            <div class="m-feedback__row">
              <div class="a-textarea" :class="{ 'a-textarea--error' : errors.feedback }">
                <label class="a-textarea__label" for="feedback-textarea" v-text="$t('feedback.feedback')"></label>
                <textarea
                  v-model="feedback"
                  class="a-textarea__textarea"
                  name="feedback"
                  :placeholder="feedbackPlaceholder"
                  id="feedback-textarea"
                  cols="30"
                  rows="4"
                  required
                />
              </div>
            </div>

            <div class="m-feedback__row">
              <div class="a-input" :class="{ 'a-input--error' : errors.name }">
                <label class="a-input__label" for="feedback-input-name" v-text="$t('feedback.name')"></label>
                <input v-model="name" class="a-input__input" type="text" id="feedback-input-name" />
              </div>
            </div>

            <div class="m-feedback__row">
              <div class="a-input" :class="{ 'a-input--error' : errors.email }">
                <label class="a-input__label" for="feedback-input-email" v-text="$t('feedback.mail')"></label>
                <input v-model="email" class="a-input__input" type="email" id="feedback-input-email" required />
              </div>
            </div>

            <div class="m-feedback__row u-mb-2">
              <button
                type="submit"
                class="a-btn"
                :class="{ 'a-btn--secondary': rating === 'worse' || rating === 'worst' }"
              >
                <span class="a-btn__label" v-text="$t('feedback.send')"></span>
                <svg class="a-btn__icon" aria-hidden="true">
                  <use :xlink:href="$root.spritePath + '#btn-arrow-right'" />
                </svg>
              </button>
            </div>

            <div class="m-feedback__row">
              <div class="o-editor-content">
                <small v-html="$t('recaptcha_text')"></small>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import { nextTick } from 'vue'

export default {
  props: {
    feedbackLabel: {
      type: String,
      required: true,
    },
    feedbackTitle: {
      type: String,
      required: true,
    },
    feedbackText: {
      type: String,
      required: true,
    },
    successTitle: {
      type: String,
      required: true,
    },
    successText: {
      type: String,
      required: true,
    },
    feedbackPlaceholder: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    formTouched: false,

    feedbackIsOpen: false,
    formHeight: 0,
    isSuccess: false,
    isError: false,

    errors: {},

    rating: '',
    name: '',
    email: '',
    feedback: '',
  }),

  mounted() {
    this.isOpen = this.opened
  },

  methods: {
    toggleFeedback() {
      this.feedbackIsOpen = ! this.feedbackIsOpen;
      this.rating = '';

      if (! this.feedbackIsOpen) {
        this.formHeight = 0
        return
      }

      this.createRecaptcha();
    },
    createRecaptcha() {
      if (document.getElementById('recaptcha-script')) {
        return;
      }

      const script = document.createElement('script');
      script.setAttribute('async', '');
      script.setAttribute('defer', '');
      script.id = 'recaptcha-script';
      script.src = `https://www.google.com/recaptcha/api.js?render=6LcPQ2cgAAAAABNWAlHML4vkDdBtin8BIARv2G8Q`;
      document.getElementsByTagName('head')[0].appendChild(script);
    },
    showForm() {
      nextTick(() => {
        this.formHeight = this.$refs.innerForm.offsetHeight + 40
      })
    },
    async submit() {
      this.isSuccess = false;
      this.isError = false;

      try {
        const token = await window.grecaptcha.execute('6LcPQ2cgAAAAABNWAlHML4vkDdBtin8BIARv2G8Q', { action: 'submit' });
        const refresh = await axios.post(window.siteUrl.replace(/\/$/, '') + '/csrf-token/refresh');

        const data = new FormData();
        data.append('rating', this.rating);
        data.append('name', this.name);
        data.append('email', this.email);
        data.append('feedback', this.feedback);
        data.append('url', window.location.href);
        data.append('recaptcha', token);
        data.append(refresh.data.csrfTokenName, refresh.data.csrfToken);

        const response = await axios.post(window.siteUrl.replace(/\/$/, '') + '/api/v1/feedback/create', data);
        if (response.data.status === 'success') {
          this.isSuccess = true;
          this.formHeight = 0;

          setTimeout(() => {
            this.feedbackIsOpen = false
          }, 5000);

          return;
        }

        this.errors = response.data.errors;
        this.isError = true;
      } catch (e) {
        this.isError = true;
        this.errors = {
          'sending': [
            this.$t('feedback.error'),
          ],
        }
        console.error(e);
      }
    },
  },
}
</script>
